import React, { useEffect } from 'react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { RefreshCw, Eye, CheckCircle, XCircle, AlertTriangle, ShieldAlert } from 'lucide-react';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import AdminTabs from '../../ui/AdminTabs';
import type { AdminTabItem } from '../../ui/AdminTabs';
import DataTableShell from '../../ui/DataTableShell';
import Badge from '../../ui/Badge';
import { WithdrawalRequest, WithdrawalFilters, Pagination } from '../../../types';
import { getWithdrawalListLabel } from '../../../utils/withdrawalRequester';
import { getWithdrawalTypeClassName, getWithdrawalTypeLabel, WITHDRAWAL_RAIL_FILTER_OPTIONS } from '../../../utils/withdrawalTypeDisplay';
import WithdrawalStatusBadge from '../withdrawalRequest/WithdrawalStatusBadge';
import WithdrawalReviewIndicator from '../withdrawalRequest/WithdrawalReviewIndicator';
import { NumberedPagination, SortableTableHead } from '@kioskgaming/ui';
import useTableSort from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import {
  AdminTableColgroup,
  ADMIN_RAW_CELL,
  ADMIN_TABLE_FIXED_CLASS,
  adminCellClass,
  WITHDRAWAL_QUEUE_COLUMNS,
  WQ,
} from '../../../config/adminTableLayout';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';
import { cn } from '../../../utils';

type StatusFilterTab = 'flagged' | 'stuck' | 'rejected' | 'all';

const STATUS_FILTER_TABS: AdminTabItem<StatusFilterTab>[] = [
  { id: 'flagged', label: 'Fraud flagged (awaiting admin)' },
  { id: 'stuck', label: 'Stuck Withdrawals' },
  { id: 'rejected', label: 'Rejected (all)' },
  { id: 'all', label: 'All statuses' },
];

const TAB_TO_STATUS: Record<StatusFilterTab, WithdrawalFilters['status'] | undefined> = {
  flagged: 'awaiting_admin',
  stuck: 'approved',
  rejected: 'rejected',
  all: undefined,
};

function statusToFilterTabId(
  status: WithdrawalFilters['status'] | undefined,
): StatusFilterTab {
  if (status === undefined) return 'all';
  if (status === 'awaiting_admin') return 'flagged';
  if (status === 'approved') return 'stuck';
  if (status === 'rejected') return 'rejected';
  return 'all';
}

interface FlaggedWithdrawalListProps {
  withdrawals: WithdrawalRequest[];
  pagination: Pagination;
  isLoading: boolean;
  isFetching?: boolean;
  appliedSort?: { sortBy: string | null; sortOrder: SortDirection | null };
  filters: WithdrawalFilters;
  onFiltersChange: React.Dispatch<React.SetStateAction<WithdrawalFilters>>;
  onViewWithdrawal: (withdrawal: WithdrawalRequest) => void;
  onApproveWithdrawal: (withdrawal: WithdrawalRequest) => void;
  onRejectWithdrawal: (withdrawal: WithdrawalRequest) => void;
  onRefresh: () => void;
}

const FlaggedWithdrawalList: React.FC<FlaggedWithdrawalListProps> = ({
  withdrawals,
  pagination,
  isLoading,
  isFetching = false,
  appliedSort,
  filters,
  onFiltersChange,
  onViewWithdrawal,
  onApproveWithdrawal,
  onRejectWithdrawal,
  onRefresh,
}) => {
  const fmt = useFormatDate();
  const isShopPlayer = (w: WithdrawalRequest) => !!(w.user?.shop_id ?? w.user?.shopId);

  const tableSort = useTableSort({
    columns: getTableSortColumns('flaggedWithdrawals'),
    onChange: () => {
      onFiltersChange((prev) => ({ ...prev, page: 1 }));
    },
  });

  useEffect(() => {
    onFiltersChange((prev) => {
      const nextSortBy = tableSort.sortParams.sortBy;
      const nextSortOrder = tableSort.sortParams.sortOrder;
      if (prev.sortBy === nextSortBy && prev.sortOrder === nextSortOrder) {
        return prev;
      }
      const next: WithdrawalFilters = { ...prev, page: 1 };
      if (nextSortBy && nextSortOrder) {
        next.sortBy = nextSortBy as WithdrawalFilters['sortBy'];
        next.sortOrder = nextSortOrder;
      } else {
        delete next.sortBy;
        delete next.sortOrder;
      }
      return next;
    });
  }, [tableSort.sortBy, tableSort.sortOrder, onFiltersChange]);

  const headerSort = tableSort.sortBy
    ? {
        activeKey: appliedSort?.sortBy ?? tableSort.sortBy,
        direction: appliedSort?.sortOrder ?? tableSort.sortOrder,
      }
    : { activeKey: null, direction: null };

  const isTableBusy = isFetching && withdrawals.length > 0;
  const getWithdrawalTypeBadge = (withdrawal: WithdrawalRequest) => (
    <Badge className={getWithdrawalTypeClassName(withdrawal.withdrawalType, withdrawal)}>
      {getWithdrawalTypeLabel(withdrawal.withdrawalType, withdrawal)}
    </Badge>
  );

  const handlePageChange = (page: number) => {
    onFiltersChange({ ...filters, page });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <CardTitle className="text-lg">
              Withdrawal queue
              {pagination.total > 0 && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  ({pagination.total} total)
                </span>
              )}
            </CardTitle>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="mt-4">
          <AdminTabs<StatusFilterTab>
            variant="pills"
            tabs={STATUS_FILTER_TABS}
            value={statusToFilterTabId(filters.status)}
            onChange={(id) =>
              onFiltersChange({ ...filters, status: TAB_TO_STATUS[id], page: 1 })
            }
            aria-label="Withdrawal status filters"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-3 mt-3 flex-wrap">
          <select
            value={filters.withdrawalType || ''}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                withdrawalType: (e.target.value || undefined) as WithdrawalFilters['withdrawalType'],
                page: 1,
              })
            }
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">All Types</option>
            {WITHDRAWAL_RAIL_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => onFiltersChange({ ...filters, dateFrom: e.target.value, page: 1 })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="From date"
          />

          <input
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => onFiltersChange({ ...filters, dateTo: e.target.value, page: 1 })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="To date"
          />
        </div>
      </CardHeader>

      <CardContent>
        {isLoading && withdrawals.length === 0 ? (
          <div className="flex justify-center py-12">
            <PageLoadingIndicator
              variant="section"
              spinnerClassName="text-muted-foreground"
              labelClassName="text-muted-foreground"
            />
          </div>
        ) : withdrawals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <ShieldAlert className="h-12 w-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">No withdrawals in this queue</p>
            <p className="text-sm">Try another tab or filter.</p>
          </div>
        ) : (
          <>
            <DataTableShell borderless>
              <table className={cn(ADMIN_TABLE_FIXED_CLASS, 'text-sm')} aria-busy={isTableBusy || undefined}>
                <AdminTableColgroup columns={WITHDRAWAL_QUEUE_COLUMNS} />
                <thead>
                  <tr className="border-b">
                    <th className={cn(ADMIN_RAW_CELL, 'text-left font-medium text-muted-foreground')}>Transaction</th>
                    <th className={cn(ADMIN_RAW_CELL, 'text-left font-medium text-muted-foreground')}>User</th>
                    <th className={cn(ADMIN_RAW_CELL, 'text-left font-medium text-muted-foreground')}>Type</th>
                    <SortableTableHead
                      sortable
                      className={ADMIN_RAW_CELL}
                      label="Amount"
                      sortKey="amount"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      align="right"
                      disabled={isTableBusy}
                    />
                    <SortableTableHead
                      sortable
                      className={ADMIN_RAW_CELL}
                      label="Status"
                      sortKey="status"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      disabled={isTableBusy}
                    />
                    <th className={cn(ADMIN_RAW_CELL, 'text-left font-medium text-muted-foreground')}>Review</th>
                    <SortableTableHead
                      sortable
                      className={ADMIN_RAW_CELL}
                      label="Date"
                      sortKey="createdAt"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      disabled={isTableBusy}
                    />
                    <th className={cn(ADMIN_RAW_CELL, 'text-right font-medium text-muted-foreground')}>Actions</th>
                  </tr>
                </thead>
                <tbody className={cn(isTableBusy && 'opacity-60 transition-opacity')}>
                  {withdrawals.map((withdrawal) => (
                    <tr
                      key={withdrawal.transactionId}
                      className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => onViewWithdrawal(withdrawal)}
                    >
                      <td className={cn(ADMIN_RAW_CELL, adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.tx]))}>
                        <div className="font-mono text-xs truncate">{withdrawal.transactionId}</div>
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.party]))}>
                        <div className="text-sm truncate">{getWithdrawalListLabel(withdrawal)}</div>
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.type]))}>
                        {getWithdrawalTypeBadge(withdrawal)}
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.amount]))}>
                        <span className="font-semibold text-red-600">
                          -${withdrawal.amount.toFixed(2)}
                        </span>
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.status]))}>
                        <WithdrawalStatusBadge status={withdrawal.status} className="px-2 py-1" />
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.review]))}>
                        <WithdrawalReviewIndicator withdrawal={withdrawal} variant="compact" />
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, 'text-xs text-muted-foreground', adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.created]))}>
                        {fmt(withdrawal.createdAt)}
                      </td>
                      <td className={cn(ADMIN_RAW_CELL, adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.actions]))}>
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewWithdrawal(withdrawal)}
                            title="View Profit Analysis"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {withdrawal.status === 'awaiting_admin' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onApproveWithdrawal(withdrawal)}
                                disabled={isShopPlayer(withdrawal)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title={isShopPlayer(withdrawal) ? 'Shop player: admin can only view (cannot approve/reject)' : 'Approve'}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onRejectWithdrawal(withdrawal)}
                                disabled={isShopPlayer(withdrawal)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title={isShopPlayer(withdrawal) ? 'Shop player: admin can only view (cannot approve/reject)' : 'Reject'}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTableShell>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} items)
                </div>
                <NumberedPagination
                  currentPage={pagination.page}
                  totalPages={pagination.pages}
                  onPageChange={handlePageChange}
                  variant="admin"
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FlaggedWithdrawalList;
